import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { BOOK_ROUTE } from "@/config/site";
import { cn } from "@/lib/utils";

// Reusable service card. Individual services route to /book; corporate
// services route to the corporate consultation form.
export const ServiceCard = ({ service, className = "" }) => {
  const isCorporate = service.bookingType === "corporate";
  const to = isCorporate ? "/corporate-wellness#consultation" : BOOK_ROUTE;
  const ctaLabel = isCorporate ? "Request a Corporate Consultation" : "Book Now";
  return (
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] transition-colors duration-500 hover:border-legacy-mint/60",
        className
      )}
      data-testid={`service-card-${service.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={service.image}
          alt={service.imageAlt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-legacy-charcoal via-legacy-charcoal/20 to-transparent" />
        <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-legacy-charcoal/70 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-legacy-mint backdrop-blur-sm">
          <MapPin className="h-3 w-3" aria-hidden="true" />
          {service.locationType}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-6 md:p-8">
        <h3 className="font-heading text-2xl md:text-3xl text-white">
          {service.name}
        </h3>
        <p className="mt-3 flex-1 text-sm md:text-base leading-relaxed text-white/65">
          {service.description}
        </p>

        <dl className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/10 pt-5 text-sm">
          <div>
            <dt className="text-white/40 text-xs uppercase tracking-wider">Duration</dt>
            <dd className="text-white/80 mt-0.5">{service.duration}</dd>
          </div>
          <div>
            <dt className="text-white/40 text-xs uppercase tracking-wider">Pricing</dt>
            <dd className="text-white/80 mt-0.5">{service.price}</dd>
          </div>
        </dl>

        <div className="mt-6">
          <Link
            to={to}
            className="inline-flex items-center gap-2 text-sm font-semibold text-legacy-mint transition-colors hover:text-white"
            data-testid={`service-book-${service.id}`}
            aria-label={`${ctaLabel} — ${service.name}`}
          >
            {ctaLabel}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};
